import React, { useState, useEffect } from 'react';
import { Machine, MachineStatus } from '../context/MachineContext';
import './MachineFormModal.css';

interface Props {
  initial?:    Partial<Machine>;
  workspaceId: string;
  onSave:      (data: Omit<Machine, 'machineId' | 'sensors' | 'createdAt'>) => void;
  onClose:     () => void;
}

const MACHINE_TYPES = ['Industrial Mixer', 'Conveyor', 'Packaging Machine', 'Pump', 'Compressor', 'Robot Arm', 'CNC Machine', 'Other'];
const STATUSES: MachineStatus[] = ['active', 'idle', 'maintenance', 'offline'];

interface FormState {
  name:      string;
  type:      string;
  location:  string;
  status:    MachineStatus;
  factoryId: string;
}

interface Errors { name?: string; type?: string; location?: string; }

const MachineFormModal: React.FC<Props> = ({ initial, workspaceId, onSave, onClose }) => {
  const [form, setForm] = useState<FormState>({
    name:      initial?.name      || '',
    type:      initial?.type      || MACHINE_TYPES[0],
    location:  initial?.location  || '',
    status:    initial?.status    || 'active',
    factoryId: initial?.factoryId || '',
  });
  const [errors, setErrors] = useState<Errors>({});

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const set = (field: keyof FormState, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const validate = (): boolean => {
    const e: Errors = {};
    if (!form.name.trim())     e.name     = 'Machine name is required';
    if (!form.type)            e.type     = 'Machine type is required';
    if (!form.location.trim()) e.location = 'Location is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    onSave({ ...form, workspaceId });
  };

  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-card">
        <div className="modal-header">
          <h2>{initial?.machineId ? '✏️ Edit Machine' : '➕ Add Machine'}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="form-row">
            <div className={`form-group ${errors.name ? 'has-error' : ''}`}>
              <label>Machine Name *</label>
              <input
                value={form.name}
                onChange={e => set('name', e.target.value)}
                placeholder="e.g. Mixer Alpha"
                autoFocus
              />
              {errors.name && <span className="field-error">{errors.name}</span>}
            </div>

            <div className={`form-group ${errors.type ? 'has-error' : ''}`}>
              <label>Type *</label>
              <select value={form.type} onChange={e => set('type', e.target.value)}>
                {MACHINE_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
              {errors.type && <span className="field-error">{errors.type}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className={`form-group ${errors.location ? 'has-error' : ''}`}>
              <label>Location *</label>
              <input
                value={form.location}
                onChange={e => set('location', e.target.value)}
                placeholder="e.g. Building A — Line 1"
              />
              {errors.location && <span className="field-error">{errors.location}</span>}
            </div>

            <div className="form-group">
              <label>Status</label>
              <select value={form.status} onChange={e => set('status', e.target.value as MachineStatus)}>
                {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Factory ID <span className="optional">(optional)</span></label>
            <input
              value={form.factoryId}
              onChange={e => set('factoryId', e.target.value)}
              placeholder="e.g. factory_cookie_main"
            />
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary">
              {initial?.machineId ? 'Save Changes' : 'Add Machine'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MachineFormModal;
